import React from 'react';
import { format } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Lock } from 'lucide-react';
import { RecordActionsMenu } from '@/shared/components/RecordActionsMenu';
import type {
  ObservationPriority,
  ObservationStatus,
  ObservationSummary,
  LocationMinimal,
} from '../types';
import { PRIORITY_LABELS, STATUS_LABELS, formatLocationLabel } from '../types';

// Solid, filled status pills.
const STATUS_BADGE: Record<ObservationStatus, string> = {
  OPEN: 'border-transparent bg-blue-600 text-white hover:bg-blue-600',
  COMPLETED: 'border-transparent bg-green-600 text-white hover:bg-green-600',
  OVERDUE: 'border-transparent bg-red-600 text-white hover:bg-red-600',
  CLOSED: 'border-transparent bg-slate-600 text-white hover:bg-slate-600',
};

// Lighter tinted priority pills so status stays the dominant signal.
const PRIORITY_BADGE: Record<ObservationPriority, string> = {
  LOW: 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  MEDIUM: 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
  HIGH: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return format(new Date(iso), 'MMM d, yyyy');
};

const COLUMN_COUNT = 10;

interface Props {
  items: ObservationSummary[];
  isLoading: boolean;
  error?: unknown;
  onRowClick: (item: ObservationSummary) => void;
  onEdit: (item: ObservationSummary) => void;
  onDelete: (item: ObservationSummary) => void;
}

export const ObservationsTable: React.FC<Props> = ({
  items,
  isLoading,
  error,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
          <TableCell key={j} className="py-2">
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <div className="min-w-0 space-y-2" data-tour-id="observations-table">
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-64 w-full rounded-md" />
          ))
        ) : error ? (
          <div className="rounded-md border px-4 py-8 text-center text-sm text-destructive">
            Failed to load observations.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border px-4 py-10 text-center text-sm text-muted-foreground">
            No observations found.
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="w-full min-w-0 rounded-md border bg-card p-4 text-left transition-colors hover:bg-muted/50"
              onClick={() => onRowClick(item)}
            >
              <div className="flex min-w-0 items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    {item.is_private && (
                      <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="font-mono text-xs text-muted-foreground">
                      OBS-{item.number}
                    </span>
                  </div>
                  <p className="mt-1 break-words text-sm font-semibold">{item.title}</p>
                </div>
                <RecordActionsMenu
                  label={`observation OBS-${item.number}`}
                  canEdit={item.can_edit}
                  canDelete={item.can_delete}
                  onView={() => onRowClick(item)}
                  onEdit={() => onEdit(item)}
                  onDelete={() => onDelete(item)}
                />
              </div>
              <div className="mt-4 grid min-w-0 grid-cols-2 gap-x-4 gap-y-3">
                <MobileCardField label="Status">
                  <Badge
                    className={`max-w-full whitespace-normal text-left text-xs ${STATUS_BADGE[item.status]}`}
                  >
                    {STATUS_LABELS[item.status]}
                  </Badge>
                </MobileCardField>
                <MobileCardField label="Priority">
                  <Badge
                    className={`max-w-full whitespace-normal text-left text-xs ${PRIORITY_BADGE[item.priority]}`}
                  >
                    {PRIORITY_LABELS[item.priority]}
                  </Badge>
                </MobileCardField>
                <MobileCardField label="Assignee">
                  {item.assignee
                    ? `${item.assignee.first_name} ${item.assignee.last_name}`
                    : 'Unassigned'}
                </MobileCardField>
                <MobileCardField label="Due Date">{formatDate(item.due_date)}</MobileCardField>
                <MobileCardField label="Type">{item.observation_type?.name ?? '—'}</MobileCardField>
                <MobileCardField label="Location">
                  <LocationValue location={item.location} />
                </MobileCardField>
                <MobileCardField label="Created">{formatDate(item.created_at)}</MobileCardField>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-medium whitespace-nowrap">#</TableHead>
              <TableHead className="text-xs font-medium">Title</TableHead>
              <TableHead className="text-xs font-medium w-[120px]">Status</TableHead>
              <TableHead className="text-xs font-medium w-[110px]">Priority</TableHead>
              <TableHead className="text-xs font-medium">Assignee</TableHead>
              <TableHead className="text-xs font-medium">Type</TableHead>
              <TableHead className="text-xs font-medium">Location</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Due Date</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Created</TableHead>
              <TableHead className="w-[72px] text-xs font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              renderSkeletonRows()
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-8 text-center text-destructive">
                  Failed to load observations.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  No observations found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <ObservationRow
                  key={item.id}
                  item={item}
                  onView={onRowClick}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

function MobileCardField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 min-w-0 break-words text-xs font-medium text-foreground">{children}</div>
    </div>
  );
}

function ObservationRow({
  item,
  onView,
  onEdit,
  onDelete,
}: {
  item: ObservationSummary;
  onView: (item: ObservationSummary) => void;
  onEdit: (item: ObservationSummary) => void;
  onDelete: (item: ObservationSummary) => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => onView(item)}>
      <TableCell className="py-1.5 text-xs font-mono text-muted-foreground whitespace-nowrap">
        OBS-{item.number}
      </TableCell>
      <TableCell className="max-w-[240px] truncate py-1.5 text-xs font-medium">
        <div className="flex items-center gap-1">
          {item.is_private && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
          <span className="truncate">{item.title}</span>
        </div>
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${STATUS_BADGE[item.status]}`}>
          {STATUS_LABELS[item.status]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${PRIORITY_BADGE[item.priority]}`}>
          {PRIORITY_LABELS[item.priority]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.assignee ? `${item.assignee.first_name} ${item.assignee.last_name}` : '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.observation_type?.name ?? '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        <LocationValue location={item.location} />
      </TableCell>
      <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">
        {formatDate(item.due_date)}
      </TableCell>
      <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">
        {formatDate(item.created_at)}
      </TableCell>
      <TableCell className="py-1.5">
        <RecordActionsMenu
          label={`observation OBS-${item.number}`}
          canEdit={item.can_edit}
          canDelete={item.can_delete}
          onView={() => onView(item)}
          onEdit={() => onEdit(item)}
          onDelete={() => onDelete(item)}
        />
      </TableCell>
    </TableRow>
  );
}

function LocationValue({ location }: { location: LocationMinimal | null }) {
  if (!location) return <>—</>;
  const areaTagSets = location.area_tag_sets ?? [];
  const areaTagSetNames = areaTagSets.map((areaTagSet) => areaTagSet.name).join(', ');

  return (
    <div className="min-w-0">
      <div className="truncate text-foreground" title={formatLocationLabel(location)}>
        {formatLocationLabel(location)}
      </div>
      {areaTagSets.length > 0 && (
        <div
          className="mt-0.5 truncate text-[11px] font-medium text-primary"
          title={areaTagSetNames}
        >
          {areaTagSets.length === 1 ? 'Phase/Zone' : 'Phases/Zones'}: {areaTagSetNames}
        </div>
      )}
    </div>
  );
}

export default ObservationsTable;
